import {
  Card,
  Col,
  Dropdown,
  Empty,
  Image,
  MenuProps,
  Row,
  Spin,
  Typography
} from 'antd';
import React from 'react';
import type { ResourceItem } from './ResourceSelector';

type ProviderInfo = {
  name: string;
  logoSrc?: string;
  count?: number;
};

type ProviderGridProps = {
  providers: ProviderInfo[];
  loading: boolean;
  onSelect?: (providerName: string) => void;
  modelsByProvider?: Record<string, ResourceItem[]>;
  onModelSelect?: (providerName: string, modelId: string) => void;
  filterTypes?: string[];
};

const { Text } = Typography;

export const ProviderGrid: React.FC<ProviderGridProps> = ({
  providers,
  loading,
  onSelect,
  modelsByProvider = {},
  onModelSelect,
  filterTypes = []
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#8c8c8c' }}>
          Loading providers from AWS Bedrock...
        </div>
      </div>
    );
  }

  if (!providers || providers.length === 0) {
    return (
      <Empty description="No providers available" style={{ padding: 40 }} />
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ maxWidth: 1400, margin: '0 auto' }}>
      {providers.map((provider) => (
        <Col key={provider.name} xs={24} sm={12} md={8} lg={8} xl={6}>
          <Dropdown
            trigger={['click']}
            placement="bottom"
            overlayStyle={{ maxHeight: 360, overflowY: 'auto', width: 'auto' }}
            menu={(() => {
              const tset = new Set(
                (filterTypes || []).map((x) => String(x).toUpperCase())
              );
              const items: MenuProps['items'] = (
                modelsByProvider[provider.name] || []
              )
                .filter((m) => {
                  if (tset.size === 0) return true;
                  const arr =
                    ((m as any).inferenceTypesSupported as string[]) || [];
                  const types = (
                    arr.length > 0
                      ? arr
                      : (m as any).inferenceType
                      ? [(m as any).inferenceType]
                      : []
                  ).map((v) => String(v).toUpperCase());
                  if (types.length === 0) return tset.size === 0;
                  return types.some((v) => tset.has(v));
                })
                .map((m) => {
                  const ctxMatch = m.id?.match(/:(\d+k)$/i);
                  const ctx = ctxMatch ? ctxMatch[1].toUpperCase() : undefined;
                  return {
                    key: m.id,
                    label: (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 8
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>{m.name}</span>
                          {!!ctx && (
                            <span
                              style={{
                                background: '#f0f0f0',
                                borderRadius: 6,
                                padding: '0 6px',
                                fontSize: 11,
                                color: '#595959'
                              }}
                            >
                              {ctx}
                            </span>
                          )}
                        </div>
                        {!!m.id && (
                          <span
                            style={{
                              color: '#8c8c8c',
                              fontSize: 12,
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap'
                            }}
                            title={m.id}
                          >
                            {m.id}
                          </span>
                        )}
                      </div>
                    )
                  };
                });

              const onClick: MenuProps['onClick'] = ({ key }) => {
                if (onModelSelect) onModelSelect(provider.name, String(key));
              };

              return {
                items,
                onClick,
                style: { width: 'auto', minWidth: 'auto' }
              } as MenuProps;
            })()}
          >
            <Card
              hoverable
              style={{
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 12
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 14,
                  padding: 12
                }}
              >
                {provider.logoSrc ? (
                  <Image
                    src={provider.logoSrc}
                    alt={`${provider.name} logo`}
                    width={72}
                    height={72}
                    preview={false}
                    style={{ objectFit: 'contain' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 8,
                      background: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8c8c8c',
                      fontWeight: 600
                    }}
                    aria-label={`${provider.name} logo placeholder`}
                  >
                    {provider.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <Text strong style={{ textAlign: 'center', fontSize: 16 }}>
                  {provider.name}
                </Text>
                {typeof provider.count === 'number' && (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {provider.count} models
                  </Text>
                )}
              </div>
            </Card>
          </Dropdown>
        </Col>
      ))}
    </Row>
  );
};

export default ProviderGrid;
